import React from 'react';
import { formatDurationFromHours } from '../../../shared/utils/numberFormatter';

/**
 * UserJourneyCards Component
 * Displays 12 stat cards in 3 rows following Cryptique design system
 */
const UserJourneyCards = ({ metrics, isLoading }) => {
  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentcolor: "#0f172a",
    borderColor: "#DDE2EE"
  };

  // Card component - matching Overview page style
  const StatCard = ({ title, value, subtitle = "Total Overall", isLoading = false }) => (
    <div className="p-4 md:p-6 text-center" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
      <div className="text-xs font-medium mb-2" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
        {title}
      </div>
      {isLoading ? (
        <div className="h-8 mb-1 animate-pulse" style={{ backgroundColor: styles.secondaryBg, borderRadius: '4px' }}></div>
      ) : (
        <div className="text-lg md:text-xl font-semibold mb-1" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
          {value}
        </div>
      )}
      <div className="text-xs" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
        {subtitle}
      </div>
    </div>
  );

  // Default/fallback values if metrics are not available
  const defaultMetrics = {
    uniqueUsers: 0,
    uniqueWallets: 0,
    web3Users: 0,
    avgPageViewsPerVisit: 0,
    avgDurationPerVisitHr: 0,
    avgSessions: 0,
    bounceRate: 0,
    transactedUsers: 0,
    avgTransactions: 0,
    avgWalletNetworth: 0
  };

  const data = metrics || defaultMetrics;

  return (
    <div className="space-y-4">
      {/* Row 1: Unique Users, Unique Wallets, Web3 Users, Avg Page Views */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Unique Users" 
          value={data.uniqueUsers?.toLocaleString() || '0'} 
          isLoading={isLoading}
        />
        <StatCard 
          title="Unique Wallets" 
          value={data.uniqueWallets?.toLocaleString() || '0'} 
          isLoading={isLoading}
        />
        <StatCard 
          title="Web3 Users" 
          value={data.web3Users?.toLocaleString() || '0'} 
          isLoading={isLoading}
        />
        <StatCard 
          title="Avg Page Views per Visit" 
          value={typeof data.avgPageViewsPerVisit === 'number' ? data.avgPageViewsPerVisit.toFixed(2) : '0.00'} 
          isLoading={isLoading}
        />
      </div>

      {/* Row 2: Avg Duration, Avg Sessions, Bounce Rate */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard 
          title="Avg Duration per Visit" 
          value={typeof data.avgDurationPerVisitHr === 'number' ? formatDurationFromHours(data.avgDurationPerVisitHr) : '0 sec'} 
          isLoading={isLoading}
        />
        <StatCard 
          title="Avg No. of Sessions" 
          value={data.avgSessions?.toLocaleString() || '0'} 
          isLoading={isLoading}
        />
        <StatCard 
          title="Bounce Rate" 
          value={typeof data.bounceRate === 'number' ? `${data.bounceRate.toFixed(2)}%` : '0.00%'} 
          isLoading={isLoading}
        />
      </div>

      {/* Row 3: Transacted Users, Avg Transactions, Avg Networth */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard 
          title="Transacted Users" 
          value={data.transactedUsers?.toLocaleString() || '0'} 
          isLoading={isLoading}
        />
        <StatCard 
          title="Avg No. of Transactions" 
          value={data.avgTransactions?.toLocaleString() || '0'} 
          subtitle="Transacted Users"
          isLoading={isLoading}
        />
        <StatCard 
          title="Avg Wallet Networth" 
          value={`$${typeof data.avgWalletNetworth === 'number' ? data.avgWalletNetworth.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '0.0'}`} 
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default UserJourneyCards;

